/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import Logo from '@/components/Logo';
import { Lock, ShieldCheck, AlertTriangle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { verifyResetToken, resetPassword } from '@/lib/passwordReset';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setErrorMsg(t('pwResetInvalidLink')); setChecking(false); return; }
      try {
        const r = await verifyResetToken(token);
        if (cancelled) return;
        setValid(true); setEmail(r.email || '');
      } catch (e) {
        setErrorMsg(e?.response?.data?.detail || t('pwResetInvalidLink'));
      } finally { if (!cancelled) setChecking(false); }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  const strength = React.useMemo(() => {
    if (!pwd) return { score: 0, label: '' };
    let s = 0;
    if (pwd.length >= 8) s++;
    if (pwd.length >= 12) s++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
    if (/\d/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    const labels = [t('pwStrengthWeak'), t('pwStrengthWeak'), t('pwStrengthMedium'), t('pwStrengthMedium'), t('pwStrengthStrong'), t('pwStrengthStrong')];
    return { score: s, label: labels[s] };
  }, [pwd, t]);

  const canSubmit = valid && pwd.length >= 8 && pwd === pwd2 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await resetPassword(token, pwd);
      setDone(true);
      toast.success(t('pwResetSuccess'));
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('genericError'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background bg-hero-gradient flex items-center justify-center px-4 py-8 relative" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }} className="w-full max-w-md relative">
        <div className="flex items-center justify-between mb-6">
          <Logo size={44} withText textClassName="text-base" tagline={t('tagline')} />
        </div>
        <Card className="p-6 rounded-2xl shadow-elev-lg">
          {checking ? (
            <div className="space-y-3" data-testid="reset-checking">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !valid ? (
            <div className="text-center py-2" data-testid="reset-invalid-view">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-destructive/15 text-destructive flex items-center justify-center">
                <AlertTriangle className="h-7 w-7" />
              </div>
              <h2 className="mt-3 font-display font-semibold text-xl">{t('pwResetInvalidTitle')}</h2>
              <p className="text-sm text-muted-foreground mt-1">{errorMsg}</p>
              <Link to="/forgot-password" className="block mt-6">
                <Button className="w-full" data-testid="reset-request-new-btn">{t('pwResetRequestNew')}</Button>
              </Link>
              <Link to="/login" className="mt-3 inline-block text-sm text-muted-foreground hover:text-foreground">{t('pwResetBackToLogin')}</Link>
            </div>
          ) : done ? (
            <div className="text-center py-2" data-testid="reset-done-view">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-accent/15 text-accent flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h2 className="mt-3 font-display font-semibold text-xl">{t('pwResetDoneTitle')}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t('pwResetDoneDesc')}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Lock className="h-5 w-5 text-primary" />
                <h1 className="font-display font-semibold text-xl">{t('pwResetNewTitle')}</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-5">{t('pwResetNewFor', { email })}</p>
              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div>
                  <Label htmlFor="rp-pwd" className="text-xs text-muted-foreground">{t('pwResetNewPassword')}</Label>
                  <div className="mt-1 relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="rp-pwd" type={show ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)} className="pl-9 pr-9" minLength={8} required data-testid="reset-password-input" autoFocus />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" data-testid="reset-toggle-visibility">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {pwd && (
                    <div className="mt-2 flex items-center gap-2" data-testid="reset-strength-indicator">
                      <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full transition-all ${strength.score <= 1 ? 'bg-destructive w-1/5' : strength.score <= 3 ? 'bg-warning w-3/5' : 'bg-accent w-full'}`} />
                      </div>
                      <span className="text-[10px] uppercase font-bold text-muted-foreground">{strength.label}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="rp-pwd2" className="text-xs text-muted-foreground">{t('pwResetConfirm')}</Label>
                  <div className="mt-1 relative">
                    <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="rp-pwd2" type={show ? 'text' : 'password'} value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="pl-9" required data-testid="reset-password2-input" />
                  </div>
                  {pwd2 && pwd !== pwd2 && <p className="text-xs text-destructive mt-1">{t('pwResetMismatch')}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={!canSubmit} data-testid="reset-submit-btn">
                  {submitting ? t('loading') : t('pwResetSetPassword')}
                </Button>
              </form>
              <p className="mt-4 text-[11px] text-muted-foreground text-center">{t('pwResetSecurityNote')}</p>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
